// src/components/OSINT/Leaks.js
//
// OSINT > Leaks tab. Part 1 of the OSINT feature area (IntelX only).
// Part 2 will add Network (Shodan) and Domains tabs alongside this one.
//
// Flow: operator types a selector (@wasabi.com, milot@wasabi.com, etc.) ->
// backend submits to IntelX + polls + fetches previews + runs Claude triage
// + dedupes findings. UI shows only meaningful findings by default; noise
// can be revealed with a toggle.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Database,
  ExternalLink,
  EyeOff,
  Loader,
  RefreshCw,
  Search as SearchIcon,
  SearchCheck,
  Settings as SettingsIcon,
  Shield,
  ShieldAlert,
  Sparkles,
  Star,
  Trash2,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { osintAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';

// ---- enum -> visual config ------------------------------------------------

const SEVERITY_CONFIG = {
  critical:  { label: 'Critical',  dot: '#ef4444', text: 'text-red-700',    bg: 'bg-red-100',    border: 'border-red-300',    rank: 5 },
  suspected: { label: 'Suspected', dot: '#f43f5e', text: 'text-rose-700',   bg: 'bg-rose-100',   border: 'border-rose-300',   rank: 4.5 },
  high:      { label: 'High',      dot: '#f97316', text: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300', rank: 4 },
  medium:   { label: 'Medium',   dot: '#eab308', text: 'text-yellow-700',  bg: 'bg-yellow-100', border: 'border-yellow-300', rank: 3 },
  low:      { label: 'Low',      dot: '#3b82f6', text: 'text-blue-700',    bg: 'bg-blue-100',   border: 'border-blue-300',   rank: 2 },
  info:     { label: 'Info',     dot: '#94a3b8', text: 'text-slate-700',   bg: 'bg-slate-100',  border: 'border-slate-300',  rank: 1 },
  noise:    { label: 'Noise',    dot: '#475569', text: 'text-slate-500',   bg: 'bg-slate-100',  border: 'border-slate-200',  rank: 0 },
};

const STATUS_CONFIG = {
  new:          { label: 'New',          color: 'text-shinobi-green' },
  acknowledged: { label: 'Acknowledged', color: 'text-blue-700' },
  actioned:     { label: 'Actioned',     color: 'text-emerald-700' },
  dismissed:    { label: 'Dismissed',    color: 'text-slate-500' },
};

const ACTION_LABELS = {
  rotate_password_immediately: 'Rotate password immediately',
  notify_employee: 'Notify the employee',
  monitor: 'Monitor for changes',
  ignore_noise: 'Ignore (noise)',
  escalate_to_security: 'Escalate to security team',
  no_action: 'No action needed',
};

const SIGNAL_LABELS = {
  credential_leak: 'Credential leak',
  pii_leak: 'PII leak',
  email_mention: 'Email mention',
  stealer_log: 'Infostealer log',
  session_token: 'Session token',
  api_key_leak: 'API key leak',
  scraped_directory: 'Scraped directory',
  whois_dump: 'WHOIS dump',
  corporate_page: 'Corporate page',
  role_account_noise: 'Role account noise',
  lead_gen_scrape: 'Lead-gen scrape',
  needs_review: 'Needs review',
};

// Empty by default = search all buckets the API key has access to. Operator
// can opt into scoping via the Advanced > Buckets chips below.
const DEFAULT_BUCKETS = [];

const ALL_BUCKETS = [
  'leaks.public.general',
  'leaks.private.general',
  'leaks.public.wikileaks',
  'leaks.logs',
  'pastes',
  'darknet.tor',
  'darknet.i2p',
  'dumpster',
  'whois',
  'usenet',
  'web.public.com',
  'web.public.org',
  'web.public.net',
  'web.public.gov',
  'documents.public.scihub',
];

// ---- helpers --------------------------------------------------------------

const fmtRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
};

const detectTermType = (term) => {
  const t = term.trim();
  if (!t) return 'auto';
  if (t.startsWith('@') || /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(t)) return 'domain';
  if (/^\S+@\S+\.\S+$/.test(t)) return 'email';
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(t)) return 'ip';
  return 'auto';
};

// ---- top-level page -------------------------------------------------------

const OsintLeaks = () => {
  const { searchId: searchIdParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPentester = user?.role === 'pentester';

  const [budget, setBudget] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [currentSearch, setCurrentSearch] = useState(null);
  const [currentFindings, setCurrentFindings] = useState([]);
  const [findingsCount, setFindingsCount] = useState({ meaningful: 0, noise: 0 });
  const [showNoise, setShowNoise] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const pollTimer = useRef(null);

  const loadBudget = useCallback(async () => {
    try {
      const r = await osintAPI.getBudget();
      setBudget(r.data);
    } catch {
      /* non-fatal */
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const r = await osintAPI.listSearches({ limit: 50 });
      setRecentSearches(r.data || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    try {
      const r = await osintAPI.getSuggestions();
      setSuggestions(r.data || null);
    } catch {
      /* non-fatal */
    }
  }, []);

  const loadSearch = useCallback(async (id) => {
    try {
      const r = await osintAPI.getSearch(id);
      const search = r.data;
      setCurrentSearch(search);
      const findings = search.findings || [];
      const noise = findings.filter((f) => f.severity === 'noise').length;
      const meaningful = findings.length - noise;
      setFindingsCount({ meaningful, noise });
      // Rank findings high -> low; noise at the bottom.
      findings.sort((a, b) => {
        const sa = SEVERITY_CONFIG[a.severity]?.rank ?? 0;
        const sb = SEVERITY_CONFIG[b.severity]?.rank ?? 0;
        if (sb !== sa) return sb - sa;
        return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
      });
      setCurrentFindings(findings);
      return search;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      return null;
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadBudget();
    loadRecent();
    loadSuggestions();
  }, [loadBudget, loadRecent, loadSuggestions]);

  // Deep-link: open a specific search by URL param
  useEffect(() => {
    if (!searchIdParam) return;
    loadSearch(Number(searchIdParam));
  }, [searchIdParam, loadSearch]);

  // Poll a running search until completed/failed
  useEffect(() => {
    if (!currentSearch) return;
    const isRunning = ['pending', 'fetching', 'triaging'].includes(currentSearch.status);
    if (!isRunning) {
      if (pollTimer.current) clearInterval(pollTimer.current);
      return;
    }
    pollTimer.current = setInterval(async () => {
      await loadSearch(currentSearch.id);
      await loadBudget();
    }, 2000);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [currentSearch?.id, currentSearch?.status, loadSearch, loadBudget]);

  const handleSearch = async ({ term, buckets, sort, maxResults, previewCap }) => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await osintAPI.createSearch({
        term,
        term_type: detectTermType(term),
        buckets,
        sort,
        max_results: maxResults,
        preview_cap: previewCap,
      });
      const search = r.data;
      navigate(`/osint/leaks/${search.id}`, { replace: false });
      await loadSearch(search.id);
      await loadRecent();
      await loadBudget();
      loadSuggestions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 503) {
        setError('IntelX is not configured. Ask an admin to set INTELX_API_KEY in the backend env, then retry.');
      } else {
        setError(detail || err.message || 'Search failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    if (!currentSearch) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await osintAPI.refreshSearch(currentSearch.id);
      navigate(`/osint/leaks/${r.data.id}`, { replace: false });
      await loadSearch(r.data.id);
      await loadRecent();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTriage = async () => {
    if (!currentSearch) return;
    if (!window.confirm(
      'Run per-bucket triage?\n\nThis spends ~6 IntelX search credits (no preview credits) to flag which accounts appear in a leak bucket as Suspected.'
    )) return;
    setError(null);
    try {
      await osintAPI.triageSearch(currentSearch.id);
      // The search flips to status=triaging; loadSearch picks that up and the
      // existing poll loop drives it to completion.
      await loadSearch(currentSearch.id);
      await loadBudget();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Triage failed');
    }
  };

  const handleFindingChange = (updated) => {
    setCurrentFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleDeleteSearch = (search) => setConfirmDelete(search);

  const doDeleteSearch = async () => {
    const search = confirmDelete;
    if (!search) return;
    try {
      await osintAPI.deleteSearch(search.id);
      await loadRecent();
      loadSuggestions();
      if (currentSearch?.id === search.id) {
        setCurrentSearch(null);
        setCurrentFindings([]);
        navigate('/osint/leaks', { replace: true });
      }
    } catch (err) {
      setToast({ kind: 'error', message: err?.response?.data?.detail || 'Delete failed' });
      setTimeout(() => setToast(null), 6000);
    }
  };

  const handleDeepSearch = async (finding) => {
    try {
      const r = await osintAPI.deepSearchFinding(finding.id);
      const newSearch = r.data;
      setToast({
        kind: 'success',
        message: `Deep search launched for ${finding.account} (search #${newSearch.id}).`,
        searchId: newSearch.id,
      });
      await loadRecent();
      await loadBudget();
      setTimeout(() => setToast((t) => (t && t.searchId === newSearch.id ? null : t)), 10_000);
    } catch (err) {
      setToast({
        kind: 'error',
        message: err?.response?.data?.detail || err.message || 'Deep search failed',
      });
      setTimeout(() => setToast(null), 6000);
    }
  };

  const visibleFindings = useMemo(() => {
    if (showNoise) return currentFindings;
    return currentFindings.filter((f) => f.severity !== 'noise');
  }, [currentFindings, showNoise]);

  return (
    <div>
      <PageHeader
        title="OSINT > Leaks"
        subtitle="Powered by intelx.io · AI-triaged by Wasabi Shinobi AI"
        icon={SearchIcon}
        actions={
          <div className="flex items-center gap-2">
            <BudgetPills budget={budget} />
            <button
              onClick={() => navigate('/osint/leaks/accounts')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cyber-300/60 text-slate-700 hover:border-shinobi-green hover:text-shinobi-green transition-colors text-sm"
              title="Browse every account with findings"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Browse accounts</span>
            </button>
            {isPentester && (
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cyber-300/60 text-slate-700 hover:border-shinobi-green hover:text-shinobi-green transition-colors text-sm"
                title="Manage noise patterns"
              >
                <SettingsIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Noise patterns</span>
              </button>
            )}
          </div>
        }
      />

      <div className="px-6 py-6 space-y-6">
        <SearchForm onSearch={handleSearch} submitting={submitting} />

        {error && (
          <div className="cyber-card rounded-lg p-4 border border-danger-red/40 bg-danger-red/5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-danger-red flex-shrink-0 mt-0.5" />
            <p className="text-danger-red text-sm">{error}</p>
          </div>
        )}

        {(recentSearches.length > 0 || suggestions) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <SearchHistory
                items={recentSearches}
                activeId={currentSearch?.id}
                onClick={(id) => navigate(`/osint/leaks/${id}`)}
                onDelete={handleDeleteSearch}
              />
            </div>
            <div>
              <SuggestionsPanel
                suggestions={suggestions}
                onOpenFinding={(fid) => {
                  // open the search that first surfaced this finding (best-effort)
                  // Operator can also just deep-search from the suggestion card.
                }}
                onDeepSearch={async (finding) => {
                  // wrap into our handleDeepSearch (needs the finding's account)
                  await handleDeepSearch({ id: finding.finding_id, account: finding.account });
                }}
                onNavigateSearch={(sid) => navigate(`/osint/leaks/${sid}`)}
              />
            </div>
          </div>
        )}

        {currentSearch && (
          <CurrentSearchPanel
            search={currentSearch}
            findings={visibleFindings}
            meaningfulCount={findingsCount.meaningful}
            noiseCount={findingsCount.noise}
            showNoise={showNoise}
            onToggleNoise={() => setShowNoise((v) => !v)}
            onRefresh={handleRefresh}
            onTriage={handleTriage}
            onFindingChange={handleFindingChange}
            onDeepSearch={handleDeepSearch}
            submitting={submitting}
          />
        )}

        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 max-w-md rounded-xl border px-4 py-3 shadow-lg flex items-start gap-3 ${
              toast.kind === 'error'
                ? 'border-red-300 bg-red-100 text-red-700'
                : 'border-wasabi-300 bg-wasabi-100 text-wasabi-700'
            }`}
            style={{ backdropFilter: 'blur(6px)' }}
          >
            {toast.kind === 'error' ? (
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            ) : (
              <SearchCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 text-xs">
              <p>{toast.message}</p>
              {toast.searchId && (
                <button
                  onClick={() => { navigate(`/osint/leaks/${toast.searchId}`); setToast(null); }}
                  className="mt-1 underline hover:no-underline"
                >
                  Open search
                </button>
              )}
            </div>
            <button onClick={() => setToast(null)} className="text-slate-500 hover:text-slate-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {!currentSearch && !error && (
          <div className="rounded-2xl border border-cyber-300/40 bg-cyber-100/30 p-10 text-center">
            <SearchIcon className="w-10 h-10 text-shinobi-green/60 mx-auto mb-3" />
            <p className="text-slate-700 text-sm">
              Search a selector (email, domain, IP) to see AI-triaged leak findings from Intelligence X.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Tip: try <code className="px-1 py-0.5 rounded bg-cyber-300/40">@wasabi.com</code> to sweep the whole domain.
            </p>
          </div>
        )}
      </div>

      {drawerOpen && (
        <NoiseSourcesDrawer onClose={() => setDrawerOpen(false)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDeleteSearch}
        title="Delete search"
        message={
          confirmDelete
            ? `The cached IntelX items for "${confirmDelete.term}" will be removed. Findings stay (they may be referenced by other searches). This cannot be undone.`
            : ''
        }
        confirmText="Delete search"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// ---- BudgetPills ----------------------------------------------------------

const BudgetPills = ({ budget }) => {
  if (!budget) return null;
  const pill = (label, used, total) => {
    const remaining = total - used;
    const low = remaining < 50;
    const danger = remaining < 10;
    return (
      <div
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono border ${
          danger
            ? 'border-red-300 bg-red-100 text-red-700'
            : low
            ? 'border-orange-300 bg-orange-100 text-orange-700'
            : 'border-cyber-300/40 bg-cyber-100/40 text-slate-700'
        }`}
        title={`${used}/${total} used today (resets midnight UTC)`}
      >
        <span className="text-slate-500">{label}</span>
        <span>{used}/{total}</span>
      </div>
    );
  };
  return (
    <div className="flex items-center gap-2">
      {pill('search', budget.search_used_today, budget.daily_search_limit)}
      {pill('preview', budget.preview_used_today, budget.daily_preview_limit)}
    </div>
  );
};

// ---- SearchForm -----------------------------------------------------------

const SearchForm = ({ onSearch, submitting }) => {
  const [term, setTerm] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [buckets, setBuckets] = useState(DEFAULT_BUCKETS);
  const [sort, setSort] = useState(2);
  const [maxResults, setMaxResults] = useState(100);
  const [previewCap, setPreviewCap] = useState(50);

  const submit = (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    onSearch({ term: term.trim(), buckets, sort, maxResults, previewCap });
  };

  const toggleBucket = (b) => {
    setBuckets((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-cyber-300/40 bg-cyber-100/30 p-5 space-y-4">
      <div className="flex items-stretch gap-3">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="@wasabi.com, milot@wasabi.com, 1.2.3.4, ..."
            className="terminal-input w-full pl-9 pr-3 py-2.5 rounded-lg text-sm"
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !term.trim()}
          className="px-5 py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          style={{
            background: term.trim() && !submitting
              ? '#48b94c'
              : '#e2e8f0',
            color: term.trim() && !submitting ? '#ffffff' : '#64748b',
            boxShadow: term.trim() && !submitting ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
          }}
        >
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
          <span>Search</span>
        </button>
      </div>

      <button
        type="button"
        onClick={() => setAdvanced((v) => !v)}
        className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
      >
        {advanced ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Advanced
      </button>

      {advanced && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-700 pt-2 border-t border-cyber-300/30">
          <div className="md:col-span-3">
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-2">
              Buckets <span className="normal-case text-slate-600 font-normal">(none selected = search ALL buckets your license has access to)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_BUCKETS.map((b) => {
                const active = buckets.includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => toggleBucket(b)}
                    className={`text-[11px] font-mono px-2 py-1 rounded border transition-colors ${
                      active
                        ? 'bg-wasabi-100 text-wasabi-700 border-wasabi-300'
                        : 'bg-cyber-100/40 text-slate-500 border-cyber-300/40 hover:border-cyber-300'
                    }`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-2">Sort</label>
            <select
              value={sort}
              onChange={(e) => setSort(Number(e.target.value))}
              className="terminal-input w-full px-2 py-1.5 rounded text-xs"
            >
              <option value={2}>Relevancy (highest first)</option>
              <option value={4}>Date (newest first)</option>
              <option value={1}>Relevancy (lowest first)</option>
              <option value={3}>Date (oldest first)</option>
              <option value={0}>No sorting</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-2">Max results</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxResults}
              onChange={(e) => setMaxResults(Math.max(1, Math.min(1000, Number(e.target.value) || 100)))}
              className="terminal-input w-full px-2 py-1.5 rounded text-xs"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-2">
              Preview cap <span className="text-slate-600">(credits)</span>
            </label>
            <input
              type="number"
              min={0}
              max={500}
              value={previewCap}
              onChange={(e) => setPreviewCap(Math.max(0, Math.min(500, Number(e.target.value) || 50)))}
              className="terminal-input w-full px-2 py-1.5 rounded text-xs"
            />
          </div>
        </div>
      )}
    </form>
  );
};

// ---- SearchHistory --------------------------------------------------------

const STATUS_BADGE = {
  pending:   { label: 'pending',   color: 'text-slate-500 border-slate-300 bg-slate-100' },
  fetching:  { label: 'fetching',  color: 'text-blue-700 border-blue-300 bg-blue-100' },
  triaging:  { label: 'triaging',  color: 'text-purple-700 border-purple-300 bg-purple-100' },
  completed: { label: 'complete',  color: 'text-emerald-700 border-emerald-300 bg-emerald-100' },
  failed:    { label: 'failed',    color: 'text-red-700 border-red-300 bg-red-100' },
};

const SearchHistory = ({ items, activeId, onClick, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, 6);

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-100/30 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-cyber-300/30">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Search history</h3>
          <span className="text-[10px] text-slate-500">({items.length})</span>
        </div>
        {items.length > 6 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800"
          >
            {expanded ? 'Show fewer' : `Show all ${items.length}`}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-xs text-slate-500 text-center">No searches yet.</div>
      ) : (
        <ul className="divide-y divide-cyber-300/20">
          {visible.map((s) => {
            const active = s.id === activeId;
            const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
            return (
              <li
                key={s.id}
                className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer transition-colors group ${
                  active ? 'bg-wasabi-100' : 'hover:bg-cyber-200/30'
                }`}
                onClick={() => onClick(s.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-mono truncate ${active ? 'text-shinobi-green' : 'text-slate-900'}`}>
                      {s.term}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{fmtRelative(s.started_at)}</span>
                    <span>·</span>
                    <span>{s.total_results} raw</span>
                    <span>·</span>
                    <span>{(s.credits_search_used || 0) + (s.credits_preview_used || 0)} credits</span>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(s); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-slate-500 hover:text-red-700"
                  title="Delete search"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


// ---- SuggestionsPanel -----------------------------------------------------

const SuggestionsPanel = ({ suggestions, onDeepSearch, onNavigateSearch }) => {
  if (!suggestions) return null;
  const pb = suggestions.phonebook_not_deep_searched || [];
  const hs = suggestions.high_severity_unattended || [];
  const rs = suggestions.refreshable_searches || [];
  const empty = pb.length === 0 && hs.length === 0 && rs.length === 0;

  return (
    <div className="rounded-2xl border border-shinobi-purple/30 bg-shinobi-purple/5 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-shinobi-purple/20">
        <Sparkles className="w-4 h-4 text-shinobi-purple" />
        <h3 className="text-sm font-semibold text-slate-900">Suggestions</h3>
      </div>

      {empty ? (
        <div className="px-4 py-6 text-xs text-slate-500 text-center">
          Nothing pressing. Run a few searches to populate.
        </div>
      ) : (
        <div className="divide-y divide-shinobi-purple/15">
          {hs.length > 0 && (
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-red-700/80 mb-1.5 font-semibold">
                High severity, unattended ({hs.length})
              </div>
              <ul className="space-y-1.5">
                {hs.slice(0, 5).map((f) => (
                  <li key={f.finding_id} className="text-xs text-slate-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                    <span className="font-mono truncate flex-1">{f.account}</span>
                    <span className="text-[10px] text-slate-500 font-mono">{f.leak_event_key}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {pb.length > 0 && (
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-shinobi-green/80 mb-1.5 font-semibold">
                Not deep-searched yet ({pb.length})
              </div>
              <ul className="space-y-1">
                {pb.slice(0, 5).map((f) => (
                  <li key={f.finding_id} className="text-xs text-slate-700 flex items-center gap-2 group">
                    <span className="font-mono truncate flex-1">{f.account}</span>
                    <button
                      onClick={() => onDeepSearch(f)}
                      className="text-[10px] text-shinobi-green opacity-60 group-hover:opacity-100 transition-opacity underline"
                    >
                      deep search
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {rs.length > 0 && (
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">
                Searches older than 7 days
              </div>
              <ul className="space-y-1">
                {rs.slice(0, 5).map((s) => (
                  <li key={s.id} className="text-xs text-slate-700 flex items-center gap-2">
                    <span className="font-mono truncate flex-1">{s.term}</span>
                    <button
                      onClick={() => onNavigateSearch(s.id)}
                      className="text-[10px] text-slate-500 opacity-60 hover:opacity-100 underline"
                    >
                      open
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- CurrentSearchPanel ---------------------------------------------------

const CurrentSearchPanel = ({
  search,
  findings,
  meaningfulCount,
  noiseCount,
  showNoise,
  onToggleNoise,
  onRefresh,
  onTriage,
  onFindingChange,
  onDeepSearch,
  submitting,
}) => {
  const isRunning = ['pending', 'fetching', 'triaging'].includes(search.status);
  const isFailed = search.status === 'failed';

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [search.id]);

  // A phonebook (domain) search yields only ungraded discovery findings -
  // surface that so the all-INFO list doesn't read as broken classification.
  const isPhonebookSearch =
    findings.length > 0 &&
    findings.filter((f) => (f.leak_event_key || '').startsWith('phonebook_')).length >=
      findings.length * 0.5;

  const pageCount = Math.max(1, Math.ceil(findings.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = findings.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
      {/* Header row */}
      <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-cyber-300/30">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-slate-900 truncate">{search.term}</span>
            {isRunning && (
              <span className="flex items-center gap-1 text-[11px] text-shinobi-green">
                <Loader className="w-3 h-3 animate-spin" /> {search.status}
              </span>
            )}
            {isFailed && (
              <span className="flex items-center gap-1 text-[11px] text-red-700">
                <AlertCircle className="w-3 h-3" /> failed
              </span>
            )}
            {search.status === 'completed' && (
              <span className="flex items-center gap-1 text-[11px] text-emerald-700">
                <Check className="w-3 h-3" /> complete
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>{fmtRelative(search.started_at)}</span>
            <span>·</span>
            <span>{search.total_results} raw · {meaningfulCount} meaningful · {noiseCount} noise</span>
            <span>·</span>
            <span>{search.credits_search_used + search.credits_preview_used} credits used</span>
            {(search.buckets || []).length > 0 && (
              <>
                <span>·</span>
                <span>{search.buckets.length} buckets</span>
              </>
            )}
          </div>
          {isFailed && search.error_message && (
            <div className="mt-2 text-xs text-red-700/80 bg-red-500/5 border border-red-500/20 rounded px-2 py-1">
              {search.error_message}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {noiseCount > 0 && (
            <button
              onClick={onToggleNoise}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-cyber-300/40 text-xs text-slate-500 hover:border-cyber-300 hover:text-slate-800 transition-colors"
            >
              <EyeOff className="w-3.5 h-3.5" />
              {showNoise ? 'Hide noise' : `Show ${noiseCount} noise`}
            </button>
          )}
          {isPhonebookSearch && (
            <button
              onClick={onTriage}
              disabled={isRunning || submitting}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-rose-300 text-xs text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-40"
              title="Probe leak buckets and flag Suspected accounts (~6 IntelX search credits, no preview credits)"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              {search.status === 'triaging' ? 'Triaging...' : search.triaged_at ? 'Re-triage' : 'Triage'}
            </button>
          )}
          <button
            onClick={onRefresh}
            disabled={isRunning || submitting}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-cyber-300/40 text-xs text-slate-500 hover:border-shinobi-green hover:text-shinobi-green transition-colors disabled:opacity-40"
            title="Re-run this exact search against IntelX (burns 1 search credit + N preview credits)"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Phonebook discovery hint */}
      {isPhonebookSearch && (
        <div className="px-5 py-2.5 bg-blue-500/5 border-b border-blue-500/20 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-blue-700 flex-shrink-0 mt-0.5" />
          {search.triaged_at ? (
            <p className="text-[11px] text-blue-700/90">
              Triaged. <span className="font-semibold text-rose-700">Suspected</span> accounts turned up in a
              leak bucket - deep-search those to confirm what leaked. The rest stay ungraded discovery (Info).
            </p>
          ) : (
            <p className="text-[11px] text-blue-700/90">
              Phonebook discovery: every result is ungraded <span className="font-semibold">Info</span> by design.
              Run <span className="font-semibold text-rose-700">Triage</span> (~6 credits, no previews) to flag which
              accounts appear in leak buckets as Suspected before spending deep-search credits.
            </p>
          )}
        </div>
      )}

      {/* Findings list */}
      <div className="divide-y divide-cyber-300/20">
        {findings.length === 0 ? (
          <div className="p-8 text-center">
            {isRunning ? (
              <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Querying Intelligence X and running AI triage...</span>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">
                <Shield className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <p>No meaningful findings for this search.</p>
                {noiseCount > 0 && !showNoise && (
                  <button onClick={onToggleNoise} className="text-shinobi-green hover:underline text-xs mt-1">
                    Show {noiseCount} noise items
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          pageItems.map((f) => (
            <FindingCard
              key={f.id}
              finding={f}
              onChange={onFindingChange}
              onDeepSearch={onDeepSearch}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {findings.length > PAGE_SIZE && (
        <div className="px-5 py-3 border-t border-cyber-300/30 flex items-center justify-between text-xs text-slate-500">
          <span>
            {safePage * PAGE_SIZE + 1}-{Math.min(findings.length, (safePage + 1) * PAGE_SIZE)} of {findings.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="flex items-center gap-1 px-2 py-1 rounded border border-cyber-300/40 text-slate-500 hover:border-cyber-300 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </button>
            <span className="text-slate-500">{safePage + 1} / {pageCount}</span>
            <button
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={safePage >= pageCount - 1}
              className="flex items-center gap-1 px-2 py-1 rounded border border-cyber-300/40 text-slate-500 hover:border-cyber-300 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- FindingCard ----------------------------------------------------------

export const FindingCard = ({ finding, onChange, onDeepSearch }) => {
  const navigate = useNavigate();
  const cfg = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.info;
  const isNoise = finding.severity === 'noise';
  const isDismissed = finding.status === 'dismissed';
  const isActioned = finding.status === 'actioned';
  // Show "Deep search" only on findings keyed to a specific email selector
  // (phonebook-discovered accounts + any prior intelligent finding). Skips
  // findings where the account is 'unknown' or a non-email string.
  const isDeepSearchable = !!finding.account && finding.account.includes('@') && finding.account !== 'unknown';
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deepBusy, setDeepBusy] = useState(false);

  const loadItems = async () => {
    if (items) return;
    setLoadingItems(true);
    try {
      const r = await osintAPI.getFinding(finding.id);
      setItems(r.data.items || []);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadItems();
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await osintAPI.patchFinding(finding.id, { status });
      onChange(r.data);
    } catch (err) {
      // optimistic revert handled by parent reload on next poll
    } finally {
      setBusy(false);
    }
  };

  const setStarred = async (starred) => {
    setBusy(true);
    try {
      const r = await osintAPI.patchFinding(finding.id, { starred });
      onChange(r.data);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async (reason) => {
    setBusy(true);
    try {
      const r = await osintAPI.dismissFinding(finding.id, { reason });
      onChange(r.data);
      setShowDismiss(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDeepSearch = async () => {
    if (!onDeepSearch) return;
    setDeepBusy(true);
    try {
      await onDeepSearch(finding);
    } finally {
      // small delay so the button shows "Launching..." long enough to register
      setTimeout(() => setDeepBusy(false), 1000);
    }
  };

  const statusCfg = STATUS_CONFIG[finding.status] || STATUS_CONFIG.new;

  return (
    <div className={`p-4 ${isNoise || isDismissed ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Severity dot + rail */}
        <div className="flex flex-col items-center pt-1">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: cfg.dot, boxShadow: 'none' }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
              {cfg.label}
            </span>
            {finding.account && finding.account.includes('@') ? (
              <button
                onClick={() => navigate(`/osint/leaks/accounts/${encodeURIComponent(finding.account)}`)}
                className="text-sm font-medium text-slate-900 hover:text-shinobi-green hover:underline break-all text-left"
                title="View this account's leak provenance trace"
              >
                {finding.account}
              </button>
            ) : (
              <span className="text-sm font-medium text-slate-900 break-all">{finding.account || 'unknown account'}</span>
            )}
            <span className="text-[11px] text-slate-500">·</span>
            <span className="text-[11px] text-slate-500">{SIGNAL_LABELS[finding.signal_type] || finding.signal_type}</span>
            <span className="text-[11px] text-slate-500">·</span>
            <span className="text-[11px] text-slate-500 font-mono">{finding.leak_event_key}</span>
            {finding.starred && <Star className="w-3.5 h-3.5 text-amber-600 fill-amber-400" />}
          </div>

          {finding.evidence_snippet && (
            <div className="mt-2 text-xs text-slate-700 font-mono bg-cyber-100/40 border border-cyber-300/30 rounded px-2.5 py-1.5 break-words">
              {finding.evidence_snippet}
            </div>
          )}

          <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
            <span className="flex items-center gap-1">
              <ArrowRight className="w-3 h-3" />
              {ACTION_LABELS[finding.recommended_action] || finding.recommended_action}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3" />
              {finding.sources_count} source{finding.sources_count === 1 ? '' : 's'}
            </span>
            <span>·</span>
            <span className={statusCfg.color}>{statusCfg.label}</span>
          </div>

          {/* Action buttons */}
          {!isNoise && (
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              <button
                onClick={handleToggle}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-slate-700 border border-cyber-300/40 hover:border-shinobi-green hover:text-shinobi-green transition-colors"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Hide sources' : `Show ${finding.sources_count} source${finding.sources_count === 1 ? '' : 's'}`}
              </button>
              {isDeepSearchable && onDeepSearch && (
                <button
                  onClick={handleDeepSearch}
                  disabled={deepBusy}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-shinobi-green border border-wasabi-300 bg-wasabi-100 hover:bg-wasabi-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`Run an Intelligent search for ${finding.account} to find actual breach files. Burns 1 search + N preview credits.`}
                >
                  {deepBusy ? <Loader className="w-3 h-3 animate-spin" /> : <SearchCheck className="w-3 h-3" />}
                  {deepBusy ? 'Launching...' : 'Deep search'}
                </button>
              )}
              {finding.status !== 'acknowledged' && finding.status !== 'actioned' && !isDismissed && (
                <button
                  onClick={() => setStatus('acknowledged')}
                  disabled={busy}
                  className="px-2 py-1 rounded text-[11px] text-blue-700 border border-blue-300 hover:bg-blue-100 transition-colors"
                >
                  Acknowledge
                </button>
              )}
              {!isActioned && !isDismissed && (
                <button
                  onClick={() => setStatus('actioned')}
                  disabled={busy}
                  className="px-2 py-1 rounded text-[11px] text-emerald-700 border border-emerald-300 hover:bg-emerald-100 transition-colors"
                >
                  Mark actioned
                </button>
              )}
              {!isDismissed && (
                <button
                  onClick={() => setShowDismiss(true)}
                  disabled={busy}
                  className="px-2 py-1 rounded text-[11px] text-slate-500 border border-slate-300 hover:bg-slate-100 transition-colors"
                >
                  Dismiss
                </button>
              )}
              <button
                onClick={() => setStarred(!finding.starred)}
                disabled={busy}
                className={`px-2 py-1 rounded text-[11px] border transition-colors ${
                  finding.starred
                    ? 'text-amber-700 border-amber-300 bg-amber-100'
                    : 'text-slate-500 border-cyber-300/40 hover:border-amber-400 hover:text-amber-700'
                }`}
                title={finding.starred ? 'Unstar' : 'Star'}
              >
                <Star className={`w-3 h-3 ${finding.starred ? 'fill-amber-300' : ''}`} />
              </button>
            </div>
          )}

          {/* Sources expander */}
          {expanded && (
            <div className="mt-3 space-y-1.5">
              {loadingItems ? (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader className="w-3 h-3 animate-spin" />
                  Loading source items...
                </div>
              ) : items && items.length > 0 ? (
                items.map((it) => <SourceItem key={it.id} item={it} />)
              ) : (
                <div className="text-xs text-slate-500 italic">No source items linked.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {showDismiss && (
        <DismissModal
          onCancel={() => setShowDismiss(false)}
          onConfirm={handleDismiss}
          busy={busy}
        />
      )}
    </div>
  );
};

// ---- SourceItem (one IntelX result row under a finding) -------------------

const SourceItem = ({ item }) => {
  const [showPreview, setShowPreview] = useState(false);
  const previewAvailable = item.preview_status === 'fetched' && item.preview_text;
  return (
    <div className="rounded border border-cyber-300/30 bg-cyber-100/20 px-3 py-2 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="px-1.5 py-0.5 rounded font-mono text-[10px] bg-shinobi-navy/60 text-slate-700 border border-cyber-300/30">
          {item.bucket || 'unknown'}
        </span>
        <span className="text-slate-500">{fmtDate(item.date)}</span>
        <span className="text-slate-600">·</span>
        <span className="text-slate-500">xscore {item.xscore}</span>
        {item.name && (
          <>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500 font-mono truncate max-w-[28ch]" title={item.name}>{item.name}</span>
          </>
        )}
        <div className="flex-1" />
        <a
          href={item.intelx_url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-shinobi-green border border-wasabi-300 hover:bg-wasabi-100 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          intelx.io
        </a>
        {previewAvailable && (
          <button
            onClick={() => setShowPreview((v) => !v)}
            className="px-1.5 py-0.5 rounded text-[10px] text-slate-500 border border-cyber-300/30 hover:border-cyber-300 hover:text-slate-800 transition-colors"
          >
            {showPreview ? 'Hide preview' : 'Preview'}
          </button>
        )}
      </div>
      {showPreview && previewAvailable && (
        <pre className="mt-2 p-2 text-[11px] font-mono text-slate-700 bg-shinobi-navy/60 rounded border border-cyber-300/20 overflow-x-auto whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
          {item.preview_text}
        </pre>
      )}
      {item.preview_status === 'skipped_budget' && (
        <div className="mt-1.5 text-[10px] text-slate-500 italic">
          Preview not fetched (budget cap). Use Refresh to re-run with a higher preview cap.
        </div>
      )}
      {item.preview_status === 'skipped_no_access' && (
        <div className="mt-1.5 text-[10px] text-slate-500 italic">
          Preview not available with this API key.
        </div>
      )}
      {item.preview_status === 'failed' && (
        <div className="mt-1.5 text-[10px] text-red-700/80 italic">
          Preview fetch failed.
        </div>
      )}
      {item.noise_prefilter_reason && (
        <div className="mt-1.5 text-[10px] text-slate-500">
          Filtered as noise: <span className="font-mono">{item.noise_prefilter_reason}</span>
        </div>
      )}
    </div>
  );
};

// ---- DismissModal ---------------------------------------------------------

const DismissModal = ({ onCancel, onConfirm, busy }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cyber-50/80 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-xl border border-cyber-300/40 bg-shinobi-navy p-5 space-y-4">
        <div className="flex items-start gap-3">
          <Trash2 className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900">Dismiss finding</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Mark this as not actionable. The finding stays in the database but is hidden from the default view.
            </p>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, e.g. 'duplicate of finding #42', 'employee left in 2022')..."
          className="terminal-input w-full px-3 py-2 rounded text-xs"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded text-xs text-slate-700 border border-cyber-300/40 hover:border-cyber-300"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={busy}
            className="px-3 py-1.5 rounded text-xs text-slate-900 bg-slate-600 hover:bg-slate-500 disabled:opacity-40"
          >
            {busy ? 'Dismissing...' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- NoiseSourcesDrawer ---------------------------------------------------

const NOISE_KINDS = [
  { value: 'role_address', label: 'Role address' },
  { value: 'lead_gen_db', label: 'Lead-gen DB' },
  { value: 'whois_aggregator', label: 'WHOIS aggregator' },
  { value: 'corporate_directory', label: 'Corporate directory' },
  { value: 'other', label: 'Other' },
];

const NoiseSourcesDrawer = ({ onClose }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newKind, setNewKind] = useState('role_address');
  const [newName, setNewName] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await osintAPI.listNoiseSources();
      setSources(r.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groupedSources = useMemo(() => {
    const map = {};
    sources.forEach((s) => {
      if (!map[s.kind]) map[s.kind] = [];
      map[s.kind].push(s);
    });
    return map;
  }, [sources]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newPattern.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await osintAPI.createNoiseSource({
        kind: newKind,
        name: newName.trim(),
        match_pattern: newPattern.trim(),
      });
      setNewName('');
      setNewPattern('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (s) => {
    setBusy(true);
    try {
      await osintAPI.patchNoiseSource(s.id, { enabled: !s.enabled });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete noise pattern "${s.name}"?`)) return;
    setBusy(true);
    try {
      await osintAPI.deleteNoiseSource(s.id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-cyber-50/80 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-shinobi-navy border-l border-cyber-300/40 overflow-y-auto">
        <div className="sticky top-0 bg-shinobi-navy border-b border-cyber-300/30 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Noise patterns</h2>
            <p className="text-[11px] text-slate-500">
              Items matching these are tagged noise before the AI triage. Saves cost on obvious junk.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleAdd} className="px-5 py-4 border-b border-cyber-300/30 space-y-2">
          <div className="text-xs font-semibold text-slate-700">Add new</div>
          <div className="grid grid-cols-3 gap-2">
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value)}
              className="terminal-input px-2 py-1 rounded text-xs"
            >
              {NOISE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              className="terminal-input px-2 py-1 rounded text-xs"
            />
            <input
              type="text"
              value={newPattern}
              onChange={(e) => setNewPattern(e.target.value)}
              placeholder="Match pattern"
              className="terminal-input px-2 py-1 rounded text-xs"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !newName.trim() || !newPattern.trim()}
            className="w-full px-3 py-1.5 rounded text-xs font-semibold bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-shinobi-green/30 disabled:opacity-40"
          >
            Add pattern
          </button>
          {error && <p className="text-xs text-red-700">{error}</p>}
        </form>

        <div className="px-5 py-4 space-y-4">
          {loading && <Loader className="w-4 h-4 animate-spin text-slate-500" />}
          {NOISE_KINDS.map(({ value, label }) => {
            const list = groupedSources[value] || [];
            if (list.length === 0) return null;
            return (
              <div key={value}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
                <div className="space-y-1">
                  {list.map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs ${
                        s.enabled
                          ? 'border-cyber-300/40 bg-cyber-100/30'
                          : 'border-slate-200 bg-slate-50 opacity-60'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 truncate">{s.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{s.match_pattern}</div>
                      </div>
                      <button
                        onClick={() => toggleEnabled(s)}
                        disabled={busy}
                        className={`px-2 py-0.5 rounded text-[10px] border ${
                          s.enabled
                            ? 'text-wasabi-700 border-wasabi-300 hover:bg-wasabi-100'
                            : 'text-slate-500 border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        {s.enabled ? 'On' : 'Off'}
                      </button>
                      <button
                        onClick={() => remove(s)}
                        disabled={busy}
                        className="text-slate-500 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OsintLeaks;
